import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, TextField, CircularProgress } from '@mui/material';
import { GridColDef, DataGrid } from '@mui/x-data-grid';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { userService } from 'components/api/users';
import { gamesService } from 'components/api/games';
import axiosInstance from 'components/api/axios';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import DatePicker from 'components/common/datePicker/datePicker';
import CustomPagination from 'components/common/pagination/pagination';
import ConfirmModal from 'components/modals/ConfirmModal';
import styles from '../userDetails.module.scss';
import tabStyles from './userTabs.module.scss';
import { Trash2 } from 'lucide-react';

interface UserFreeSpinsProps {
  userId: string;
  siteId: string | number;
}

const GiveFreeSpin: React.FC<{
  userId: string;
  siteId: string | number;
  onSpinGranted: () => void;
}> = ({ userId, siteId, onSpinGranted }) => {
  const dispatch = useAppDispatch();
  const [games, setGames] = useState<any[]>([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [loadingMoreGames, setLoadingMoreGames] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [gameId, setGameId] = useState<string | null>(null);
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [freespinsQuantity, setFreespinsQuantity] = useState('2');
  const [betLevel, setBetLevel] = useState('');

  // Set default validUntil to exactly 25 days from now
  const defaultValidUntil = new Date();
  defaultValidUntil.setDate(defaultValidUntil.getDate() + 25);
  const [validUntil, setValidUntil] = useState<string>(defaultValidUntil.toISOString());

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [betLevelsLoading, setBetLevelsLoading] = useState(false);
  const [availableBetLevels, setAvailableBetLevels] = useState<any[]>([]);

  const fetchGames = useCallback(
    async (pageNumber: number, append = false) => {
      try {
        if (append) setLoadingMoreGames(true);
        else setLoadingGames(true);

        const response = await axiosInstance.get('/admin/games/free-spins', {
          params: {
            page: pageNumber,
            limit: 10,
            sortField: 'id',
            sortValue: 'DESC',
            siteId,
          },
        });

        let gamesData: any[] = [];
        let next = false;

        if (Array.isArray(response.data)) {
          gamesData = response.data;
        } else if (response.data?.data?.docs) {
          gamesData = response.data.data.docs;
          next =
            response.data.data.hasNextPage || pageNumber < (response.data.data.totalPages || 1);
        } else if (response.data?.docs) {
          gamesData = response.data.docs;
          next = response.data.hasNextPage || pageNumber < (response.data.totalPages || 1);
        } else if (Array.isArray(response.data?.data)) {
          gamesData = response.data.data;
        }

        setGames((prev) => (append ? [...prev, ...gamesData] : gamesData));
        setHasNextPage(next);
        setPage(pageNumber);
      } catch (err) {
        console.error('Failed to fetch free spin games', err);
      } finally {
        if (append) setLoadingMoreGames(false);
        else setLoadingGames(false);
      }
    },
    [siteId],
  );

  useEffect(() => {
    fetchGames(1);
  }, [fetchGames]);

  useEffect(() => {
    let ignore = false;

    const fetchBetLevels = async () => {
      setBetLevel('');
      if (!gameId || !currencyCode) {
        setAvailableBetLevels([]);
        return;
      }
      setBetLevelsLoading(true);
      try {
        const res = await gamesService.getGameBetLevels(gameId, siteId, currencyCode);
        if (ignore) return;

        if (res?.data?.currencies?.[0]?.betLevels) {
          const levels = res.data.currencies[0].betLevels;
          setAvailableBetLevels(levels);

          // Set the first bet level as default if the current betLevel is not in the list
          setBetLevel((prevBetLevel) => {
            if (
              levels.length > 0 &&
              !levels.find((l: any) => String(l.betLevel) === prevBetLevel)
            ) {
              return String(levels[0].betLevel);
            }
            return prevBetLevel;
          });
        } else {
          setAvailableBetLevels([]);
          setBetLevel('');
        }
      } catch (err) {
        if (ignore) return;
        console.error('Failed to fetch bet levels', err);
        setAvailableBetLevels([]);
        setBetLevel('');
      } finally {
        if (!ignore) {
          setBetLevelsLoading(false);
        }
      }
    };

    fetchBetLevels();

    return () => {
      ignore = true;
    };
  }, [gameId, currencyCode, siteId]);

  const handleLoadMore = () => {
    if (!loadingMoreGames && hasNextPage) {
      fetchGames(page + 1, true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameId || !freespinsQuantity || !currencyCode || !validUntil || !betLevel) return;

    // Validate betLevel is present in availableBetLevels
    if (!availableBetLevels.find((l: any) => String(l.betLevel) === betLevel)) {
      dispatch(showToast({ text: 'Please select a valid bet level', type: 'error' }));
      return;
    }

    // Validate 30 days limit
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const selectedDate = new Date(validUntil);

    if (selectedDate > thirtyDaysFromNow) {
      dispatch(
        showToast({ text: 'Valid Until cannot be more than 30 days from now', type: 'error' }),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        gameIds: [gameId],
        currencyCode,
        freespinsQuantity: Number(freespinsQuantity),
        betLevel: Number(betLevel),
        validUntil,
        reason,
      };

      await userService.giveUserSpin(userId, siteId, payload);
      dispatch(showToast({ text: 'Free spins granted successfully', type: 'success' }));

      // Reset some fields
      setFreespinsQuantity('2');
      setReason('');

      // Add a slight delay to ensure DB consistency before refetching
      setTimeout(() => {
        onSpinGranted();
      }, 300);
    } catch (error: any) {
      console.error('Failed to grant spin:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to grant spin',
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={tabStyles.formPanel}>
      <Typography
        variant="h6"
        sx={{ color: 'var(--text-primary)', mb: 3, fontWeight: 600, fontSize: '1.1rem' }}
      >
        Give Free Spin
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Select Game
            </Typography>
            {loadingGames ? (
              <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', mt: 1, mb: 2 }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 2,
                  mb: 1,
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 4,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: 4,
                  },
                }}
              >
                {games.map((game) => (
                  <Box
                    key={game.id}
                    onClick={() => {
                      setGameId(String(game.id));
                      setAvailableBetLevels([]);
                      setBetLevelsLoading(true);
                    }}
                    sx={{
                      minWidth: 120,
                      width: 120,
                      borderRadius: '8px',
                      border: `2px solid ${String(game.id) === String(gameId) ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: 'var(--bg-input)',
                      position: 'relative',
                      opacity: String(game.id) === String(gameId) ? 1 : 0.6,
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      '&:hover': {
                        opacity: 1,
                        borderColor:
                          String(game.id) === String(gameId)
                            ? 'var(--accent-blue)'
                            : 'var(--text-secondary)',
                      },
                    }}
                  >
                    <Box sx={{ width: '100%', height: 120, overflow: 'hidden' }}>
                      <img
                        src={game.imageUrl}
                        alt={game.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'https://via.placeholder.com/120?text=No+Image';
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor:
                          String(game.id) === String(gameId) ? 'var(--accent-blue)' : 'transparent',
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '0.75rem',
                          color:
                            String(game.id) === String(gameId) ? 'white' : 'var(--text-primary)',
                          textAlign: 'center',
                          fontWeight: String(game.id) === String(gameId) ? 600 : 400,
                        }}
                      >
                        {game.title}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {hasNextPage && (
                  <Box
                    onClick={handleLoadMore}
                    sx={{
                      minWidth: 120,
                      width: 120,
                      borderRadius: '8px',
                      border: '2px dashed var(--border-color)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderColor: 'var(--text-secondary)',
                      },
                    }}
                  >
                    {loadingMoreGames ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Typography
                        sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 500 }}
                      >
                        Load More
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '120px', flex: 1 }}
          >
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Currency
            </Typography>
            <TextField
              size="small"
              value={currencyCode}
              onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
              placeholder="USD"
              required
              fullWidth
              disabled
              className={tabStyles.customInput}
            />
          </Box>

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '120px', flex: 1 }}
          >
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Quantity
            </Typography>
            <TextField
              size="small"
              value={freespinsQuantity}
              onChange={(e) => setFreespinsQuantity(e.target.value.replace(/\D/g, ''))}
              placeholder="2"
              required
              fullWidth
              className={tabStyles.customInput}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', mt: 1 }}>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Bet Level
            </Typography>
            {betLevelsLoading ? (
              <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', mt: 1, mb: 1 }} />
            ) : availableBetLevels.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 3,
                  },
                }}
              >
                {availableBetLevels.map((bl) => {
                  const isSelected = String(betLevel) === String(bl.betLevel);
                  return (
                    <Button
                      key={bl.betLevel}
                      size="small"
                      onClick={() => setBetLevel(String(bl.betLevel))}
                      variant={isSelected ? 'contained' : 'outlined'}
                      sx={{
                        minWidth: 'max-content',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.8rem',
                        bgcolor: isSelected ? 'var(--accent-blue)' : 'transparent',
                        color: isSelected ? '#fff' : 'var(--text-primary)',
                        borderColor: isSelected ? 'transparent' : 'rgba(255,255,255,0.2)',
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: isSelected
                            ? 'var(--accent-blue-hover)'
                            : 'rgba(255,255,255,0.05)',
                          borderColor: isSelected ? 'transparent' : 'rgba(255,255,255,0.3)',
                        },
                      }}
                    >
                      Level {bl.betLevel} ({bl.amount})
                    </Button>
                  );
                })}
              </Box>
            ) : gameId ? (
              <Typography
                sx={{
                  color: 'var(--accent-red, #ff4d4f)',
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                  mb: 1,
                }}
              >
                No bet levels available for this game. Cannot grant free spins.
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                  mb: 1,
                }}
              >
                Select a game to view available bet levels
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: '220px',
              flex: 1,
            }}
            className={tabStyles.customInput}
          >
            <DatePicker
              label="Valid Until"
              value={validUntil}
              onChange={(val) => setValidUntil(val)}
              disablePast={true}
            />
          </Box>

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '300px', flex: 2 }}
          >
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Reason (Optional)
            </Typography>
            <TextField
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (Optional)"
              fullWidth
              className={tabStyles.customInput}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', pt: '28px' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isSubmitting ||
                !gameId ||
                betLevelsLoading ||
                !betLevel ||
                availableBetLevels.length === 0
              }
              sx={{
                backgroundColor: '#2a8039',
                color: 'white',
                textTransform: 'none',
                height: '40px',
                px: 4,
                '&:hover': { backgroundColor: '#1e682c' },
                '&.Mui-disabled': {
                  backgroundColor: 'var(--text-secondary)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isSubmitting ? 'Granting...' : 'Give Spin'}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

const UserFreeSpins: React.FC<UserFreeSpinsProps> = ({ userId, siteId }) => {
  const dispatch = useAppDispatch();
  const [spins, setSpins] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [spinToCancel, setSpinToCancel] = useState<any>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const fetchSpins = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await userService.getUserSpins(userId, siteId, {
        sortField: 'id',
        sortValue: 'DESC',
        limit: limit,
        page: page,
      });
      setSpins(response?.data?.docs || []);
      setTotal(response?.data?.totalDocs || 0);
    } catch (error) {
      console.error('Failed to fetch spins:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, siteId, limit, page]);

  useEffect(() => {
    fetchSpins();
  }, [fetchSpins]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'gameId', headerName: 'Game ID', width: 100 },
    {
      field: 'gameTitle',
      headerName: 'Game',
      flex: 1,
      minWidth: 180,
      headerClassName: 'align-left-header',
      cellClassName: 'align-left-cell',
    },
    { field: 'freespinsQuantity', headerName: 'Quantity', width: 100 },

    {
      field: 'validUntil',
      headerName: 'Valid Until',
      width: 150,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        let color = 'var(--text-primary)';
        if (status === 'used' || status === 'active') color = 'var(--accent-green)';
        else if (status === 'pending') color = 'var(--yellow-win, #ffd700)';
        else if (status === 'canceled' || status === 'cancelled') color = '#ff4d4d';

        return (
          <Typography
            sx={{ color, fontSize: '0.85rem', fontWeight: 500, textTransform: 'capitalize' }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 150 },
    {
      field: 'issuedAt',
      headerName: 'Issued At',
      width: 150,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = String(params.row.status ?? '').toLowerCase();
        if (status === 'active' || status === 'pending') {
          return (
            <Button
              size="small"
              variant="contained"
              className={styles.deleteBtn}
              onClick={(e) => {
                e.currentTarget.blur();
                setSpinToCancel(params.row);
              }}
            >
              <Trash2 size={14} />
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const handleCancelConfirm = async () => {
    if (!spinToCancel) return;
    setIsCanceling(true);
    try {
      const res = await userService.cancelUserSpin(userId, siteId, spinToCancel.id);
      dispatch(
        showToast({
          text: res.message || 'Free spin cancelled successfully',
          type: 'success',
        }),
      );
      setSpinToCancel(null);
      fetchSpins();
    } catch (error: any) {
      console.error('Failed to cancel spin:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to cancel spin',
          type: 'error',
        }),
      );
    } finally {
      setIsCanceling(false);
    }
  };

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
      rowsPerPageOptions={[5, 10, 20, 50]}
    />
  );

  return (
    <Box className={styles.tabContent}>
      <GiveFreeSpin userId={userId} siteId={siteId} onSpinGranted={fetchSpins} />

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1.1rem' }}
        >
          Spins History
        </Typography>
      </Box>

      {isLoading && !spins.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          {renderPagination()}
          <DataGrid
            rows={spins}
            rowCount={total}
            columns={columns}
            getRowId={(row) => row.id || row._id || Math.random()}
            sx={commonGridStyles}
            autoHeight
            disableColumnMenu
            hideFooterPagination
          />
          {renderPagination()}
        </Box>
      )}

      <ConfirmModal
        open={Boolean(spinToCancel)}
        title="Cancel Free Spin"
        description={`Are you sure you want to cancel the free spin (ID: ${spinToCancel?.id})? This action cannot be undone.`}
        onClose={() => setSpinToCancel(null)}
        onConfirm={handleCancelConfirm}
        confirmText="Cancel Spin"
        cancelText="Keep Spin"
        isDanger={true}
        loading={isCanceling}
      />
    </Box>
  );
};

export default UserFreeSpins;
